import { useTheme, themes, ThemeColor } from './ThemeProvider';
import { Check, Palette } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

interface ThemeSelectorProps {
  isOpen?: boolean;
  onClose: () => void;
  standalone?: boolean; // Add standalone mode for when not using Dialog
}

export function ThemeSelector({ isOpen = true, onClose, standalone = false }: ThemeSelectorProps) {
  const { theme, setTheme } = useTheme();

  const handleThemeChange = (newTheme: ThemeColor) => {
    setTheme(newTheme);
  };

  const themeGrid = (
    <>
      <div className="grid grid-cols-2 gap-3 py-4">
        {(Object.keys(themes) as ThemeColor[]).map((themeKey) => {
          const themeData = themes[themeKey];
          const isSelected = theme === themeKey;
          
          return (
            <button
              key={themeKey}
              onClick={() => handleThemeChange(themeKey)}
              className={`relative flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all hover:scale-105 ${
                isSelected 
                  ? `${themeData.border} shadow-lg` 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {isSelected && (
                <div className={`absolute -top-2 -right-2 ${themeData.primarySolid} text-white rounded-full p-1`}>
                  <Check className="h-4 w-4" />
                </div>
              )}
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${themeData.primary} shadow-md`} />
              <span className="text-sm font-medium">{themeData.name}</span>
            </button>
          );
        })}
      </div>
      <Button onClick={onClose} className="w-full">
        Apply Theme
      </Button>
    </>
  );

  // If standalone, render without Dialog wrapper
  if (standalone) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Choose Your Theme</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Personalize your experience with beautiful color themes
        </p>
        {themeGrid}
      </div>
    );
  }

  // Default: render with Dialog wrapper
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Choose Your Theme
          </DialogTitle>
          <DialogDescription>
            Personalize your experience with beautiful color themes
          </DialogDescription>
        </DialogHeader>
        {themeGrid}
      </DialogContent>
    </Dialog>
  );
}